// eslint-disable-next-line max-classes-per-file
import { IAttribute, IModel, IViolation } from './annotation';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { Metamodel } from './model';
import { INamedLoadError } from './INamedLoadError';
import { LoadingStatusEnum, ResourceStatus } from '../../interfaces/IResourceFilesProvider';

/*
    Owned represents a object in the metamodel that can be contained
    Any owner has a implicit owner relationship attached.
*/

@Metamodel.model({
    abstract: true
})
export abstract class Owned {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        transient: true
    })
    private _owner?: Owned;

    public get owner(): Owned | undefined {
        return this._owner;
    }

    public static create<T extends Owned>(model: IModel, owner: Owned): T {
        const owned = model.create() as T;
        owned._owner = owner;
        return owned;
    }

    public static own(parentOwned: Owned, property: string, owned?: Owned): void {
        parentOwned.own(property, owned);
    }

    public get conceptOwner(): Owned | undefined {
        let aowner = this._owner;
        while (aowner && !Metamodel.modelOf(aowner).concept) {
            aowner = aowner.owner;
        }

        return aowner;
    }

    public get concept(): Owned | undefined {
        let concept: Owned | undefined = this;
        while (concept && !Metamodel.modelOf(concept).concept) {
            concept = concept.owner;
        }

        return concept;
    }

    /*
        Returns the root entity of the owning tree, if it does not have an owner it returns itself.
    */

    public get rootOwner(): Owned {
        let root: Owned = this;
        while (root.owner) {
            root = root.owner;
        }
        return root;
    }

    /*
        This is a abstraction leakage but for usage is far improved by calling named
    */
    public get named(): Named | undefined {
        let named: Owned | undefined = this;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        while (named && !Named.MODEL.isInstance(named)) {
            named = named.owner;
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return named instanceof Named ? (named as Named) : undefined;
    }

    /*
          Whether this object directly or indirectly owns the other object
     */
    public owns(owned: Owned): boolean {
        while (owned.owner) {
            if (owned.owner === this) {
                return true;
            }
            owned = owned.owner;
        }
        return false;
    }

    protected own(property: string, owned?: Owned): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const oldOwned = (this as any)[property] as Owned;
        if (oldOwned === owned) {
            return;
        }

        if (oldOwned && oldOwned instanceof Owned) {
            oldOwned._owner = undefined;
        }

        if (owned) {
            owned._owner = this;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[property] = owned;
    }

    /*
      Problem with ES5 super.owner does not work.
    */
    protected getOwner(): Owned | undefined {
        return this._owner;
    }
}

/*
  Any object that is named having a name and description
*/
@Metamodel.model({
    abstract: true
})
export abstract class Named extends Owned {
    public static readonly MODEL: IModel;
    public static readonly OBJECT_ID: IAttribute;

    /*
       Attributes that define the lifecycle of this named
    */
    public static readonly LIFECYCLE = 'lifecycle';

    private static _sequencer = 0;

    public displayName?: string;

    // #region Fabric only
    /**
     * Object Id of the named
     */
    @Metamodel.attribute({
        templatable: true,
        association: 'OBJECT_ID',
    })
    public objectId?: string;

    /**
     * ID of the workspace where named lives.
     */
    public workspaceObjectId?: string;

    public extraTridentInfo?: {
        artifactType: string;
        folderObjectId: string;
        capacityObjectId: string;
        ownerUser?: {
            name: string;
        };
        modifiedByUser?: {
            userPrincipalName: string;
        };
    };

    public isFakeEntityCreatedFromReference?: boolean = false; // true means the object is not a truly-existing entity and created from reference when tolerateInvalidReference is true
    // #endregion

    @Metamodel.attribute({
        display: shellStringResources.Name
    })
    public name?: string;

    /**
     * Has to be modelled so it can carried forward in copy etc?
     */
    @Metamodel.attribute({
        constraint: {
            validator: Named.JSONValidator
        }
    })
    public raw?: string = '{}';

    /**
     * Indicates what went wrong during loading of this model.
     */
    public loadError: INamedLoadError = {
        status: LoadingStatusEnum.Ok
    };

    public status: ResourceStatus = 'Ok';
    public isGitMismatch: boolean = false;

    @Metamodel.attribute({
        transient: true
    })
    private _id: number = 0;

    @Metamodel.attribute({
        transient: true
    })
    private _extension = {};

    constructor(name?: string, public description?: string, id?: number) {
        super();
        this.name = name;
        this._id = !id ? Named.nextSequence() : id;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public static JSONValidator(named: Named): IViolation[] | undefined {
        if (named.raw) {
            try {
                // try to parse the raw properties
                JSON.parse(named.raw);
            } catch (error) {
                return [
                    {
                        context: named,
                        message: () =>
                            sanitizeAndFormatString(shellStringResources.InvalidJSONValidationMessage, named.name)
                    }
                ];
            }
        }
        return undefined;
    }

    /*
      Internal API only
    */
    public static get idProperty(): string {
        return '_id';
    }

    private static nextSequence(): number {
        return ++Named._sequencer;
    }

    /*
     * Immutable transient identifier to the object. Model guarantees that this is unique.
     * It is app unique but because of copies made of the same object, the id will reappear.
     * It is primarily used to key into a object which is keyed by name which is susceptible to changes
     */
    get id(): number {
        return this._id;
    }

    /*
      Framework extensions can be added to any named object
    */
    get extension(): { [key: string]: {} } {
        return this._extension;
    }

    /**
     * The named was loaded fine.
     */
    public get isOk(): boolean {
        return this.status === 'Ok';
    }

    /**
     * Indicates of the named was built from an improper payload. (e.g. Malformed JSON, type mismatches, ...)
     */
    public get isBad(): boolean {
        return this.status === 'Bad';
    }

    /**
     * The named is ok but references another named which is in a bad state.
     */
    public get isDependentOnBadResource(): boolean {
        return this.status === 'BadDependency';
    }

    /*
      This clears the content of the object. All top level objects should have a method to clear
      to go from fetched to listed status
    */
    // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
    public clear(): void {}

    public equals(named: Named): boolean {
        return this === named;
    }
}

/**
 *  A object that can react to changes. All objects that have
 *  references can write specific logic if they need to do something
 *  to react to a change from another reusable object. The framework calls
 *  this on the nearest named object in the chain which is reactor
 *  The framework calls the owner chain until it hits a reusable object after
 *  which it find dependencies on other reusable objects
 */
export interface IReactor {
    /*
     *   Default action to change is no-op.
     *   Return true indicates the said object was changed
     *   You can indicate that in the object state flags
     */
    react(objects?: Map<Named, boolean>): boolean;
}
